import { Fragment } from 'react';
import { Trans } from 'react-i18next';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { Checkbox } from '@components/Checkbox';
import { Select } from '@components/Select';
import { Switch } from '@components/Switch';
import Tooltip from '@components/Tooltip';
import { ExternalLink } from '@components/Link';
import Alert from '@components/Toaster/Alert';
import { useTranslation } from '@hooks';
import { CustomizeRow } from './styles';
import {
    CREATOR_INFO_STATUS,
    TIKTOK_ISSUE,
    TIKTOK_PRIVACY,
    hasVideo,
    isPhotoPost,
    resolvePrivacyChoice,
    withBrandedContent,
    withCommercialContent,
} from '../tiktokRules';

const MUSIC_USAGE_URL = 'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en';
const BRANDED_CONTENT_POLICY_URL = 'https://www.tiktok.com/legal/page/global/bc-policy/en';

const Section = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
});

const OptionRow = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControlLabel-root': {
        marginLeft: '-3px',
        marginRight: 0,
    },
    '& .MuiFormControlLabel-label': {
        fontSize: 14,
        color: 'var(--text-primary)',
    },
});

const Caption = ({ children, color = 'var(--text-secondary)' }) => (
    <Typography variant="caption" sx={{ color, display: 'block' }}>
        {children}
    </Typography>
);

const LabelledCheckbox = ({ label, checked, onChange, disabled, disabledReason }) => {
    const control = (
        <FormControlLabel
            control={<Checkbox checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />}
            label={label}
        />
    );
    if (!disabledReason) return control;
    return (
        <Tooltip title={disabledReason} placement="top-start">
            <span style={{ display: 'inline-flex' }}>{control}</span>
        </Tooltip>
    );
};

export const TikTokTabNotices = ({ creatorInfo }) => {
    const { t } = useTranslation();
    const { status, isAudited, refetch } = creatorInfo;
    return (
        <>
            {status === CREATOR_INFO_STATUS.READY && !isAudited && (
                <Alert severity="info" sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                        {t('SocialPosts.tiktok.unauditedBanner')}
                    </Typography>
                </Alert>
            )}
            {status === CREATOR_INFO_STATUS.ERROR && (
                <Alert severity="warning" sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                        {t('SocialPosts.tiktok.creatorInfoFailed')}{' '}
                        <ExternalLink
                            href="#"
                            isBlank={false}
                            onClick={e => {
                                e.preventDefault();
                                refetch();
                            }}
                        >
                            {t('SocialPosts.tiktok.retry')}
                        </ExternalLink>
                    </Typography>
                </Alert>
            )}
        </>
    );
};

const TikTokSettings = ({ disabled, tiktok, media }) => {
    const { t } = useTranslation();
    const { options, onChange, constraints, creatorInfo, issues, autoAddMusic, onAutoAddMusicChange } = tiktok;
    const photoPost = isPhotoPost(media);
    const loading = creatorInfo.status !== CREATOR_INFO_STATUS.READY;
    const branded = !!options.commercialContent && !!options.brandedContent;
    const hasIssue = code => issues.some(i => i.code === code);

    const privacyLabel = level => t(`SocialPosts.tiktok.privacyLevels.${level}`);
    const privacy = resolvePrivacyChoice(options, constraints, { readOnly: disabled, loading });
    const privacyOptions = privacy.levels.map(level => {
        const blocked = privacy.selectable && branded && level === TIKTOK_PRIVACY.SELF_ONLY;
        return {
            value: level,
            label: privacyLabel(level),
            disabled: blocked,
            tooltip: blocked ? t('SocialPosts.tiktok.brandedNotPrivate') : undefined,
        };
    });
    const privacyNeedsChoice =
        !disabled &&
        !loading &&
        (hasIssue(TIKTOK_ISSUE.PRIVACY_REQUIRED) || hasIssue(TIKTOK_ISSUE.PRIVACY_UNAVAILABLE));

    const patch = next => onChange({ ...options, ...next });

    // Off everywhere means the control is unavailable; off somewhere means the post keeps the
    // choice and only those accounts publish without it.
    const interaction = (key, allowKey, disabledBy, disabledForAll) => (
        <Fragment key={key}>
            <LabelledCheckbox
                label={t(`SocialPosts.tiktok.${key}`)}
                checked={!!options[allowKey] && !disabledForAll}
                onChange={checked => patch({ [allowKey]: checked })}
                disabled={disabled || loading || disabledForAll}
                disabledReason={
                    disabledForAll
                        ? t('SocialPosts.tiktok.interactionDisabled', { handle: disabledBy.join(', ') })
                        : null
                }
            />
            {!disabledForAll && disabledBy.length > 0 && (
                <Caption>
                    {t('SocialPosts.tiktok.interactionDisabledForSome', { handle: disabledBy.join(', ') })}
                </Caption>
            )}
        </Fragment>
    );

    const mediaWord = t(photoPost ? 'SocialPosts.tiktok.mediaWordPhoto' : 'SocialPosts.tiktok.mediaWordVideo');

    return (
        <>
            <Section>
                <Select
                    value={privacy.value}
                    onChange={({ target: { value } }) => patch({ privacyLevel: value })}
                    label={t('SocialPosts.tiktok.privacy')}
                    options={privacyOptions}
                    fullWidth
                    required
                    disabled={disabled || loading || constraints.noCommonOption}
                    error={privacyNeedsChoice}
                    helperText={
                        privacyNeedsChoice
                            ? t('SocialPosts.tiktok.privacyChooseAgain')
                            : t('SocialPosts.tiktok.privacyHint')
                    }
                />
            </Section>

            <Section>
                <Typography variant="body2" color="textPrimary">
                    {t('SocialPosts.tiktok.interactions')}
                </Typography>
                <OptionRow>
                    {interaction(
                        'allowComment',
                        'allowComment',
                        constraints.commentDisabledBy,
                        constraints.commentDisabledForAll,
                    )}
                    {!photoPost &&
                        interaction(
                            'allowDuet',
                            'allowDuet',
                            constraints.duetDisabledBy,
                            constraints.duetDisabledForAll,
                        )}
                    {!photoPost &&
                        interaction(
                            'allowStitch',
                            'allowStitch',
                            constraints.stitchDisabledBy,
                            constraints.stitchDisabledForAll,
                        )}
                </OptionRow>
            </Section>

            <Section>
                <CustomizeRow>
                    <Switch
                        checked={!!options.commercialContent}
                        handleChange={e => onChange(withCommercialContent(options, e.target.checked))}
                        label={t('SocialPosts.tiktok.commercial')}
                        disabled={disabled}
                    />
                </CustomizeRow>
                <Caption>{t('SocialPosts.tiktok.commercialHint')}</Caption>
                {options.commercialContent && (
                    <OptionRow>
                        <LabelledCheckbox
                            label={t('SocialPosts.tiktok.yourBrand')}
                            checked={!!options.yourBrand}
                            onChange={checked => patch({ yourBrand: checked })}
                            disabled={disabled}
                        />
                        <LabelledCheckbox
                            label={t('SocialPosts.tiktok.brandedContent')}
                            checked={!!options.brandedContent}
                            onChange={checked => onChange(withBrandedContent(options, checked))}
                            disabled={disabled}
                        />
                        {branded ? (
                            <Caption>{t('SocialPosts.tiktok.labelPaidPartnership', { media: mediaWord })}</Caption>
                        ) : options.yourBrand ? (
                            <Caption>{t('SocialPosts.tiktok.labelPromotional', { media: mediaWord })}</Caption>
                        ) : (
                            <Caption color="var(--error-main)">{t('SocialPosts.tiktok.commercialIncomplete')}</Caption>
                        )}
                    </OptionRow>
                )}
            </Section>

            {!hasVideo(media) && (
                <Section>
                    <CustomizeRow>
                        <Switch
                            checked={autoAddMusic}
                            handleChange={e => onAutoAddMusicChange(e.target.checked)}
                            label={t('SocialPosts.tiktokAutoAddMusic')}
                            disabled={disabled}
                        />
                    </CustomizeRow>
                    <Caption>{t('SocialPosts.tiktokAutoAddMusicHint')}</Caption>
                </Section>
            )}

            <Caption>
                {t('SocialPosts.tiktok.processingNote')}{' '}
                <Trans
                    t={t}
                    i18nKey={branded ? 'SocialPosts.tiktok.consentBranded' : 'SocialPosts.tiktok.consent'}
                    components={{
                        musicLink: <ExternalLink href={MUSIC_USAGE_URL} variant="caption" />,
                        bcLink: <ExternalLink href={BRANDED_CONTENT_POLICY_URL} variant="caption" />,
                    }}
                />
            </Caption>
        </>
    );
};

export default TikTokSettings;
